import { useState } from "react";
import { Car, Sparkles, Star, Gem, Plus, Check } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { PACKAGES, ADDONS, VEHICLE_TYPES } from "../../data";

const ICONS = { car: Car, sparkles: Sparkles, star: Star, gem: Gem };

export default function Packages({ onBook }) {
  const [type, setType] = useState("Sedan");

  return (
    <section id="packages" className="bg-cream py-20 sm:py-28">
      <div className="mx-auto max-w-6xl px-5">
        <Reveal>
          <Eyebrow>Services &amp; pricing</Eyebrow>
          <div className="mt-4 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
            <h2 className="font-display text-3xl leading-tight sm:text-5xl">Pick your package</h2>
            <div
              data-testid="vehicle-type-toggle"
              className="inline-flex rounded-full border border-ink/20 p-1"
            >
              {VEHICLE_TYPES.map((t) => (
                <button
                  key={t}
                  data-testid={`toggle-${t.toLowerCase()}`}
                  onClick={() => setType(t)}
                  className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors ${
                    type === t ? "bg-ink text-cream" : "text-ink/60"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {PACKAGES.map((p, i) => {
            const Icon = ICONS[p.icon];
            const price = p.prices[type];
            const plus = p.truckPlus && type === "Truck";
            return (
              <Reveal key={p.id} delay={i * 0.08}>
                <div
                  data-testid={`package-card-${p.id}`}
                  className="relative flex h-full flex-col rounded-3xl bg-ink p-7 text-cream"
                >
                  {p.badge && (
                    <span className="absolute right-6 top-6 rounded-full bg-cream px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-ink">
                      {p.badge}
                    </span>
                  )}
                  <Icon size={24} className="text-cream/90" />
                  <h3 className="mt-5 font-display text-2xl leading-tight">{p.name}</h3>
                  <p className="mt-2 text-sm text-cream/60">{p.desc}</p>

                  <div className="mt-6 flex items-baseline gap-1">
                    <span className="font-display text-5xl">${price}</span>
                    {plus && <span className="font-display text-3xl">+</span>}
                    <span className="ml-2 text-xs uppercase tracking-widest text-cream/50">{type}</span>
                  </div>

                  <ul className="mt-6 flex-1 space-y-2.5">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-cream/80">
                        <Check size={16} className="mt-0.5 shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>

                  <button
                    data-testid={`book-package-${p.id}`}
                    onClick={() => onBook({ packageId: p.id, vehicleType: type })}
                    className="mt-7 rounded-full bg-cream py-3.5 text-sm font-bold uppercase tracking-widest text-ink transition-transform active:scale-95"
                  >
                    Book {p.name.split(" ")[0]}
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-6 rounded-3xl border border-ink/15 p-7">
            <div className="flex items-center gap-3">
              <Plus size={20} />
              <h3 className="font-display text-xl">Add-ons</h3>
            </div>
            <div className="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2">
              {ADDONS.map((a) => (
                <div key={a.id} className="flex items-center justify-between border-b border-ink/10 pb-3">
                  <span className="font-semibold">{a.name}</span>
                  <span className="text-sm font-bold tracking-wide text-ink/60">{a.range}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
